import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { StatisticsChartCardFullscreenAction } from '../../common/statistic-chart-card/StatisticsChartCardActions';
import { StatisticsChartCardToolbar } from '../../common/statistic-chart-card/StatisticsChartCardActions';
import OverdueChart from './OverdueChart';
import DashboardOverdueRankList from './DashboardOverdueRankList';
import { buildOverdueChartConfigKey, buildOverdueChartRows, findOverdueRankItemByKey } from './lib/overdueStatChartData';
import {
	DASHBOARD_OVERDUE_RANK_CHART_CONFIG,
	DASHBOARD_OVERDUE_RANK_DIMENSION_OPTIONS,
	normalizeOverdueChartConfig,
	OVERDUE_CHART_CUSTOMER_BAR_ORIENTATION,
	OVERDUE_CHART_DIMENSION,
	OVERDUE_CHART_RANGE_MODE,
} from './lib/overdueChartConfig';

const DashboardOverdueRankPanel = ({
	overdueData = {},
	isLoading = false,
	activeDimension = OVERDUE_CHART_DIMENSION.CUSTOMER,
	onDimensionChange,
	barHeight = '12rem',
	fillSlot = false,
	onFullscreen,
	className = '',
	title = '超期统计排行',
	hint = '仅超期未付尾款 · 按应收账款排序',
	lockDimension = false,
	enableOrderDrill = false,
	onDrillSelect,
}) => {
	const overdueConfig = useMemo(
		() =>
			normalizeOverdueChartConfig({
				...DASHBOARD_OVERDUE_RANK_CHART_CONFIG,
				dimension: activeDimension,
				rangeMode: OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY,
				barOrientation: OVERDUE_CHART_CUSTOMER_BAR_ORIENTATION,
			}),
		[activeDimension],
	);

	const animationKey = useMemo(() => buildOverdueChartConfigKey(overdueConfig, 'dashboard-overdue-rank'), [overdueConfig]);

	const rankItems = useMemo(() => buildOverdueChartRows(overdueData, overdueConfig).items, [overdueConfig, overdueData]);
	const [selectedRankKey, setSelectedRankKey] = useState(null);

	useEffect(() => {
		setSelectedRankKey(null);
	}, [animationKey, activeDimension]);

	const handleSelectRankRow = useCallback(
		(key) => {
			setSelectedRankKey(key ?? null);
			if (!enableOrderDrill || !onDrillSelect || !key) return;
			const row = findOverdueRankItemByKey(rankItems, key);
			const orderIds = Array.isArray(row?.orderIds) ? row.orderIds : [];
			if (!row || !orderIds.length) return;
			onDrillSelect({
				kind: 'overdue',
				orderIds,
				label: row.label,
			});
		},
		[enableOrderDrill, onDrillSelect, rankItems],
	);

	const handleDimensionChange = useCallback(
		(dimension) => {
			setSelectedRankKey(null);
			onDimensionChange?.(dimension);
		},
		[onDimensionChange],
	);

	return (
		<div
			className={[
				'statistics-dimension-panel statistics-dashboard-overdue-rank',
				fillSlot ? 'statistics-dimension-panel--fill-slot statistics-dashboard-overdue-rank--fill-slot' : null,
				className,
			]
				.filter(Boolean)
				.join(' ')}
		>
			<div className='statistics-dimension-panel__head'>
				<div>
					<h3 className='statistics-dimension-panel__title mb-0'>{title}</h3>
					<p className='statistics-dimension-panel__hint mb-0'>{hint}</p>
				</div>
				<StatisticsChartCardToolbar>
					{lockDimension ? null : (
						<div className='statistics-primary-phase-dimension statistics-primary-phase-dimension--compact' role='group' aria-label='超期排行维度'>
							<span className='statistics-primary-phase-dimension__label'>排行维度</span>
							<div className='statistics-primary-phase-dimension__group'>
								{DASHBOARD_OVERDUE_RANK_DIMENSION_OPTIONS.map(({ value, label }) => (
									<button
										key={value}
										type='button'
										className={['statistics-primary-phase-dimension__btn', activeDimension === value ? 'is-active' : null].filter(Boolean).join(' ')}
										onClick={() => handleDimensionChange(value)}
										aria-pressed={activeDimension === value}
									>
										{label}
									</button>
								))}
							</div>
						</div>
					)}
					{onFullscreen ? <StatisticsChartCardFullscreenAction onClick={onFullscreen} title='全屏查看超期排行' /> : null}
				</StatisticsChartCardToolbar>
			</div>
			<div className='statistics-dashboard-overdue-rank__body'>
				<div className='statistics-dashboard-overdue-rank__chart'>
					<OverdueChart
						overdueData={overdueData}
						overdueConfig={overdueConfig}
						isLoading={isLoading}
						height={barHeight}
						fillHeight={fillSlot}
						compact
						hideCategoryLabels
						animationKey={animationKey}
						selectedKey={selectedRankKey}
						onSelectRow={handleSelectRankRow}
						enableOrderDrill={enableOrderDrill}
						onDrillSelect={onDrillSelect}
					/>
				</div>
				<DashboardOverdueRankList
					items={rankItems}
					overdueData={overdueData}
					isLoading={isLoading}
					activeDimension={activeDimension}
					fillSlot={fillSlot}
					selectedKey={selectedRankKey}
					onSelectRow={handleSelectRankRow}
					rangeMode={overdueConfig.rangeMode}
					className='statistics-dashboard-overdue-rank__list'
				/>
			</div>
		</div>
	);
};

DashboardOverdueRankPanel.propTypes = {
	overdueData: PropTypes.object,
	isLoading: PropTypes.bool,
	activeDimension: PropTypes.string,
	onDimensionChange: PropTypes.func,
	barHeight: PropTypes.string,
	fillSlot: PropTypes.bool,
	onFullscreen: PropTypes.func,
	className: PropTypes.string,
	title: PropTypes.string,
	hint: PropTypes.string,
	lockDimension: PropTypes.bool,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
};

export default memo(DashboardOverdueRankPanel);
